import React, { useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import type { NavigationItem } from '../types';

interface DashboardSelectorProps {
  availableDashboards: NavigationItem[];
  currentDashboard?: NavigationItem;
  onSelectDashboard: (dashboardId: string) => void;
  isLoading?: boolean;
}

export const DashboardSelector: React.FC<DashboardSelectorProps> = ({
  availableDashboards,
  currentDashboard,
  onSelectDashboard,
  isLoading = false
}) => {
  const [isOpen, setIsOpen] = useState(false);

  if (availableDashboards.length <= 1) {
    return null; // No need for selector if only one dashboard
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-foreground bg-card border border-orange-200 dark:border-orange-500/25 rounded-lg hover:bg-orange-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <div className="flex items-center gap-2">
          {currentDashboard && (
            <>
              <currentDashboard.icon className="w-4 h-4 text-orange-500" />
              <span className="truncate">{currentDashboard.label}</span>
            </>
          )}
        </div>
        <ChevronDown 
          className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-0 right-0 z-20 mt-2 bg-card border border-orange-200 dark:border-orange-500/25 rounded-lg shadow-lg overflow-hidden">
            <div className="max-h-64 overflow-y-auto">
              {availableDashboards.map((dashboard) => {
                const isSelected = dashboard.id === currentDashboard?.id;
                const Icon = dashboard.icon;
                
                return (
                  <button
                    key={dashboard.id}
                    onClick={() => {
                      onSelectDashboard(dashboard.id);
                      setIsOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors duration-200 ${
                      isSelected
                        ? 'bg-gradient-to-r from-orange-50 dark:from-orange-500/[0.08] to-amber-50 dark:to-amber-500/[0.05] text-orange-700'
                        : 'text-foreground hover:bg-orange-50/50'
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${
                      isSelected ? 'text-orange-500' : 'text-muted-foreground'
                    }`} />
                    <div className="flex-1 text-left">
                      <div className="font-medium">{dashboard.label}</div>
                      {dashboard.dashboardMetadata?.description && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {dashboard.dashboardMetadata.description}
                        </div>
                      )}
                    </div>
                    {isSelected && (
                      <Check className="w-4 h-4 text-orange-500" />
                    )}
                  </button>
                );
              })}
            </div>
            
            {availableDashboards.length > 5 && (
              <div className="px-3 py-2 text-xs text-muted-foreground bg-muted border-t border-border">
                {availableDashboards.length} dashboards available
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};